import React, {useEffect} from "react";
import * as api from "api";
import axios from "axios";
import {useCurrentUserAuthorizations} from "./query";
import {useQueryClient} from "@tanstack/react-query";

const AuthContext = React.createContext(null);


export const useAuth = () => {
    return React.useContext(AuthContext);
}

const setAuthToken = token => {
    if (token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    else {
        delete axios.defaults.headers.common["Authorization"];
    }
}

/*
 * Parse the JWT token and return the payload.
 */
// const parseJwt = (token) => {
//     const base64Url = token.split('.')[1];
//     const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
//     const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
//         return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
//     }).join(''));
//
//     return JSON.parse(jsonPayload);
// }

function AuthProvider({ children }) {

    const queryClient = useQueryClient();

    /*
     * AUTHENTICATION LOGIC
     */
    const token = localStorage.getItem('token');
    // if axios header doesn't have token, set it
    if (token && !axios.defaults.headers.common["Authorization"]) {
        setAuthToken(token);
    }

    const [isAuthenticated, setIsAuthenticated] = React.useState(!!token);

    const login = async (username, password, rememberMe) => {
        const data = await api.Auth.login(username, password, rememberMe, {skipGlobalErrorHandler: true});
        const token = data.access_token;
        localStorage.setItem('token', token);
        setAuthToken(token);
        setIsAuthenticated(true);
    };

    const logout = async (callback) => {
        await api.Auth.logout({skipGlobalErrorHandler: true});
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setAuthToken(null);
        callback();
        queryClient.clear();
    };
    /*
     * END OF AUTHENTICATION LOGIC
     */

    /*
     * AUTHORIZATION LOGIC
     */
    const authorizationsQuery = useCurrentUserAuthorizations({enabled: isAuthenticated});
    const authorizationsLoaded = !authorizationsQuery.isLoading && !authorizationsQuery.isError;
    const workgroupAuthorization = authorizationsQuery.data ?? {};

    /*
     * END OF AUTHORIZATION LOGIC
     */

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response && error.response.status === 401 && originalRequest.skipRefreshToken !== true) {
                    // Prevent more than two refresh attempts for the original request
                    originalRequest.skipRefreshToken = true;

                    // Try refreshing the token
                    try {
                        // skipRefreshToken=true in the refresh endpoint prevent infinite loop when the refresh
                        // token is expired and we get 401 on the refresh endpoint
                        const res = await api.Auth.refresh({skipRefreshToken: true});

                        const accessToken = res.access_token;
                        setAuthToken(accessToken);
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                        console.info("Token refreshed");
                        return axios(originalRequest);
                    } catch (refreshError) {
                        console.info("Refresh token failed. Logging out.", refreshError)
                        logout(f => f)
                    }
                }

                // if (error.response && error.response.status === 403) {
                //     console.log('User logged out due to a 403 error.');
                //     logout(f => f)
                // }
                return Promise.reject(error);
            }
        );

        return () => {
            // Eject the interceptor when the component unmounts
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    const context = {
        isAuthenticated,
        login,
        logout,
        authorizationsLoaded,
        workgroupAuthorization
    };

    return <AuthContext.Provider value={context}>
        {children}
    </AuthContext.Provider>;
}

export default AuthProvider;

